import { AvantArteLogo, Spinner } from '@design-system';
import { GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';

import Layout from '../../components/Layout';
import { buildSignUpFlow } from '../../features/auth';
import { AuthEntryPoint, AuthFlow } from '../../features/auth/types';
import { imageOgStaticLoader } from '../../helpers/og-image';
import { useAuth, useFlow } from '../../providers';
import { pageTranslations } from '../../utils/translations';

const Page = ({ locale, locales, ogImageUrl }: PageProps) => {
  const router = useRouter();
  const { t } = useTranslation('signinSignup');

  const { closeFlow, currentStepId, isFlowShown, startFlow } = useFlow();
  const { isLoggedIn, user } = useAuth();

  // This is a hack to prevent the page from flashing the old auth UI before redirecting to the new auth UI
  const [isLoaded, setIsLoaded] = useState(false);
  useEffect(() => void setTimeout(() => void setIsLoaded(true), 100), []);

  useEffect(() => {
    if (!user) {
      startFlow({
        name: AuthFlow.SignUp,
        steps: buildSignUpFlow({ optInGeneral: false }),
        hideCloseButton: true,
        analyticsProperties: { source: AuthEntryPoint.AuthPage },
      });
    }
  }, [user]);

  useEffect(() => {
    if (isLoggedIn) {
      if (currentStepId === 'authMethod') closeFlow();
      if (!isFlowShown) router.push('/');
    }
  }, [isLoggedIn, isFlowShown, currentStepId]);

  if (!isLoaded) return null;

  return (
    <Layout
      description={t('signup.pageDescription')}
      hideFooter
      hideHeader
      locale={locale}
      locales={locales}
      ogImageUrl={ogImageUrl}
      title={t('signup.pageTitle')}
    >
      <main className="flex flex-grow">
        <div
          className="relative flex h-screen w-full flex-col items-center justify-center text-white"
          style={{
            'backgroundImage': 'url("/signup-background.jpg")',
            'backgroundPosition': 'center center',
            'backgroundSize': 'cover',
            'backgroundRepeat': 'no-repeat',
          }}
        >
          <div className="absolute top-0 h-screen w-full bg-neutral-6/40" />

          <div className="fixed left-0 right-0 top-0 flex h-18 w-full items-center justify-center border-b border-b-white/20 bg-transparent">
            <AvantArteLogo className="max-md:hidden" variant="wordmark" />
            <AvantArteLogo className="md:hidden" variant="icon" />
          </div>

          {isLoggedIn ? <Spinner size="lg" /> : null}
        </div>
      </main>
    </Layout>
  );
};

export const getStaticProps: GetStaticProps = async ({ locale, locales }) => {
  const ogImageUrl = await imageOgStaticLoader({});

  return {
    props: {
      locale,
      locales,
      ogImageUrl,
      ...(await pageTranslations(locale!, ['signinSignup'])),
    },
  };
};

export default Page;
